// API helper for the PataTasks backend Worker.
//
// The backend is the Worker, on its own domain. The site is separately deployed
// to Cloudflare Pages at patatasks.app and serves only the interface.
const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "/api";

async function apiFetch<T>(endpoint: string, options?: RequestInit, retries = 2): Promise<T> {
  const { headers: optionHeaders, ...restOptions } = options ?? {};

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15000);

  try {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...restOptions,
      signal: controller.signal,
      headers: {
        "Content-Type": "application/json",
        ...(optionHeaders ?? {}),
      },
      credentials: "include",
    });

    clearTimeout(timeout);

    const isPaymentInitialization = endpoint === "/intasend/initiate";

    if (!response.ok && retries > 0 && response.status >= 500 && !isPaymentInitialization) {
      await new Promise((resolve) => setTimeout(resolve, 500));
      return apiFetch<T>(endpoint, options, retries - 1);
    }

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}`;
      let rawBody = "";

      try {
        const error = (await response.json()) as {
          error?: string;
          details?: string;
          message?: string;
        };
        errorMessage = error.error || error.message || errorMessage;
        if (error.details) {
          errorMessage += `: ${error.details}`;
        }
      } catch {
        try {
          if ((response as any).text) {
            rawBody = await (response as any).text();
            if (rawBody.trim()) errorMessage = rawBody.trim();
          }
        } catch {
          // keep default error message
        }
      }

      console.error("API request failed:", {
        endpoint,
        status: response.status,
        statusText: response.statusText,
        body: rawBody || "N/A",
      });

      throw new Error(errorMessage);
    }

    return response.json();
  } catch (error) {
    clearTimeout(timeout);
    if (error instanceof Error && error.name === "AbortError") {
      throw new Error("Request timed out. Please check your connection and try again.");
    }
    throw error;
  }
}

// -------------------- HEALTH --------------------

export async function checkHealth(): Promise<{ status: string; database: unknown }> {
  return apiFetch("/health");
}

// -------------------- USERS --------------------

export interface CreateUserInput {
  fullName: string;
  email?: string;
  password: string;
  phone?: string;
}

export interface User {
  id: string;
  fullName: string;
  email?: string | null;
  phone?: string | null;
  createdAt: string;
  isAdmin?: number;
  balance?: number;
  maintenancePaid?: number;
  accountLocked?: number;
  lockThreshold?: number;
  onboardingComplete?: number;
  avatarFileId?: string | null;
  avatarUrl?: string | null;
}

export async function createUser(input: CreateUserInput): Promise<{ user: User }> {
  return apiFetch("/users", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function getUser(id: string): Promise<{ user: User }> {
  return apiFetch(`/users/${id}`);
}

export async function listUsers(): Promise<{ users: User[] }> {
  return apiFetch("/users");
}

// -------------------- AUTH --------------------

export interface LoginInput {
  identifier: string;
  password: string;
}

export async function loginUser(input: LoginInput): Promise<{ user: User }> {
  return apiFetch("/auth", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function logoutUser(): Promise<{ success: boolean }> {
  return apiFetch("/logout", {
    method: "POST",
  });
}

export async function deleteUserAccount(
  deleteWord: string,
): Promise<{ success: boolean; message?: string }> {
  return apiFetch("/users", {
    method: "DELETE",
    body: JSON.stringify({ deleteWord }),
  });
}

export async function getAuthStatus(): Promise<{ user: User | null }> {
  return apiFetch("/auth");
}

// -------------------- TRANSACTIONS --------------------

export interface Transaction {
  id: string;
  type: "withdrawal";
  description: string;
  amount: number;
  date: string;
  status: "success" | "pending" | "failed";
  method?: string;
}

export type WithdrawalMethod = "mobile_money" | "bank" | "paypal";

export interface WithdrawalRequest {
  method: WithdrawalMethod;
  phone?: string;
  fullName?: string;
  bankName?: string;
  accountNumber?: string;
  paypalEmail?: string;
}

export interface CreateTransactionInput {
  userId: string;
  type: string;
  description: string;
  amount: number;
}

export async function getTransactions(
  userId: string,
  options?: { autoPromote?: boolean },
): Promise<{ transactions: Transaction[] }> {
  const params = new URLSearchParams({ userId });
  if (options?.autoPromote) {
    params.append("autoPromote", "true");
  }
  return apiFetch(`/transactions?${params.toString()}`);
}

export async function createTransaction(
  input: CreateTransactionInput,
): Promise<{ transaction: Transaction }> {
  return apiFetch("/transactions", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

// -------------------- WITHDRAWAL --------------------

export async function withdraw(
  userId: string,
  amount: number,
  withdrawal: WithdrawalRequest,
): Promise<{ transaction: Transaction }> {
  const description =
    withdrawal.method === "bank"
      ? `Bank Withdrawal to ${withdrawal.fullName || withdrawal.bankName || "bank account"}`
      : withdrawal.method === "paypal"
        ? `PayPal Withdrawal to ${withdrawal.paypalEmail || "PayPal account"}`
        : `M-Pesa Withdrawal to ${withdrawal.phone || "mobile number"}`;

  return apiFetch("/transactions", {
    method: "POST",
    body: JSON.stringify({
      userId,
      type: "withdrawal",
      method: withdrawal.method,
      phone: withdrawal.phone,
      fullName: withdrawal.fullName,
      bankName: withdrawal.bankName,
      accountNumber: withdrawal.accountNumber,
      paypalEmail: withdrawal.paypalEmail,
      description,
      amount: -amount,
    }),
  });
}

// -------------------- 💰 M-PESA INTEGRATION --------------------

export interface MpesaStkPushInput {
  userId: string;
  phone: string;
  amount: number;
  type: "mpesa_payment";
  description?: string;
}

export async function initiateMpesaPayment(input: MpesaStkPushInput): Promise<{
  success: boolean;
  checkoutRequestId: string;
  merchantRequestId: string;
  message?: string;
}> {
  return apiFetch("/mpesa/stkpush", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

// -------------------- 💳 INTASEND INTEGRATION --------------------

export interface IntaSendInitializeInput {
  amount: number; // in selected currency (USD, KES, etc.)
  email: string;
  phone?: string;
  currency?: string;
  metadata?: {
    description?: string;
    type?: string;
  };
}

export interface IntaSendInitializeOutput {
  success: boolean;
  authorizationUrl: string;
  reference: string;
  invoiceId: string;
  message?: string;
}

export async function initiateIntaSendPayment(
  input: IntaSendInitializeInput,
): Promise<IntaSendInitializeOutput> {
  return apiFetch("/intasend/initiate", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function checkIntaSendPaymentStatus(reference: string): Promise<{
  status: "pending" | "success" | "failed";
  reference?: string;
}> {
  return apiFetch(`/intasend/status?reference=${encodeURIComponent(reference)}`);
}

// -------------------- PASSWORD --------------------

export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<{ success: boolean }> {
  return apiFetch("/password/change", {
    method: "POST",
    body: JSON.stringify({ currentPassword, newPassword }),
  });
}

export async function requestPasswordReset(identifier: string): Promise<{ found: boolean }> {
  return apiFetch("/password/reset/request", {
    method: "POST",
    body: JSON.stringify({ identifier }),
  });
}

export async function confirmPasswordReset(
  identifier: string,
  newPassword: string,
): Promise<{ success: boolean }> {
  return apiFetch("/password/reset/confirm", {
    method: "POST",
    body: JSON.stringify({ identifier, newPassword }),
  });
}

// -------------------- ACCOUNT --------------------

export interface WalletAccount {
  balance: number;
}

export async function getWalletAccount(): Promise<{ account: WalletAccount }> {
  const data = await apiFetch<{ user: { balance?: number } }>("/auth");
  return { account: { balance: Number(data.user?.balance ?? 0) } };
}

export interface WithdrawInput {
  amount: number;
  method: string;
  phone?: string;
  fullName?: string;
  bankName?: string;
  accountNumber?: string;
  paypalEmail?: string;
}

export async function withdrawFromWallet(
  input: WithdrawInput,
): Promise<{ success: boolean; amount: number }> {
  return apiFetch("/withdraw", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

// -------------------- MY BIDS --------------------

export interface MyBid {
  id: string;
  gigTitle: string;
  status: string;
  date: string;
  amount: number;
}

export async function getMyBids(): Promise<{ applications: MyBid[] }> {
  return apiFetch("/my-bids");
}

// -------------------- TEST SUBMISSIONS --------------------

export interface TestSubmission {
  id: string;
  testId: string;
  testTitle: string;
  status: string;
  submittedAt: string;
  rewardAmount: number;
}

export async function getMySubmissions(): Promise<{ submissions: TestSubmission[] }> {
  return apiFetch("/submissions");
}

export async function submitTest(input: {
  testId: string;
  testTitle: string;
  answers: Record<string, string>;
  reward: number;
}): Promise<{ success: boolean; submissionId: string; reward: number }> {
  return apiFetch("/submissions", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

// -------------------- AVATAR UPLOAD --------------------

export async function uploadAvatar(file: File): Promise<{ success: boolean; avatarUrl: string }> {
  const formData = new FormData();
  formData.append("avatar", file);

  const response = await fetch("/api/avatar", {
    method: "POST",
    credentials: "include",
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || "Upload failed");
  }

  return response.json();
}

export async function deleteAvatar(): Promise<{ success: boolean }> {
  return apiFetch("/avatar", { method: "DELETE" });
}
